/**
 * Talent screen view-model (pure, DOM-free, unit-tested): node states for the grid, tier gates,
 * loadout validation messages, saved-loadout edits on the profile (adapter: progression has no
 * "save loadout" op) and a describer that says exactly what a node changes on which button, in
 * the ability tooltips' WoW-style wording (hud/tooltip).
 */
import type { AbilityDef, AuraDef, ClassDef, ClassId, HotbarKey } from '@keystone/sim';
import type { ClassTransform, LoadoutError, Profile, TalentId, TalentLoadout, TalentNode, TalentPresetId } from '@keystone/progression';
import { classRenderCtx, describeAbility } from '../../hud/tooltip/describe';
import { diffAbility } from '../../hud/tooltip/diff';
import { auraClauses, type RenderCtx } from '../../hud/tooltip/effects';
import type { TooltipContent, TooltipModel } from '../../hud/tooltip/types';
import { fmtDuration, sentence } from '../../hud/tooltip/format';
import {
  CAPSTONE_GATE,
  RANK_TIERS,
  applyTransforms,
  findAura,
  TALENT_TIER_GATES,
  defaultLoadout,
  isCapstone,
  pointsSpent,
  presetLoadout,
  profileStanding,
  roleOf,
  talentNode,
  talentTree,
  tierDef,
  validateLoadout,
} from '@keystone/progression';

// ---------------------------------------------------------------------------
// Node states
// ---------------------------------------------------------------------------

export type NodeState = 'locked' | 'available' | 'taken' | 'excluded';

export type LockReason =
  | { kind: 'tier'; tier: number; spent: number; needed: number }
  | { kind: 'capstone'; spent: number; needed: number }
  | { kind: 'rank'; tier: number; rankName: string }
  | { kind: 'prereq'; prereq: TalentId; name: string }
  | { kind: 'points'; cost: number; remaining: number }
  | { kind: 'choice'; group: string; takenId: TalentId; takenName: string };

export interface NodeView {
  node: TalentNode;
  state: NodeState;
  /** Why a not-taken node cannot be picked (empty when available). */
  reasons: LockReason[];
  /** Validation errors on a TAKEN node (the loadout is invalid because of it). */
  errors: LoadoutError[];
}

const nameOf = (id: TalentId): string => talentNode(id)?.name ?? id;

/** Name of the first rank tier that allows talent tier `t` (e.g. 3 → "Champion"). */
export function rankNameForTalentTier(t: number): string {
  return RANK_TIERS.find((r) => r.talentTiers >= t)?.name ?? 'a higher rank';
}

/**
 * State of one node given the current picks. `points` = hero talent points, `maxTier` = talent
 * tiers allowed by rank. Errors that belong to OTHER nodes of an already invalid loadout are
 * ignored, so one bad pick doesn't grey out the whole tree.
 */
export function nodeView(classId: ClassId, nodes: readonly TalentId[], nodeId: TalentId, points: number, maxTier: number): NodeView {
  const node = talentNode(nodeId);
  if (!node || node.classId !== classId) throw new Error(`nodeView: ${nodeId} is not a ${classId} talent`);
  const lo = (ids: readonly TalentId[]): TalentLoadout => ({ id: 'view', name: 'view', classId, nodes: [...ids] });

  if (nodes.includes(nodeId)) {
    const errors = validateLoadout(lo(nodes), points, maxTier).filter((e) => 'nodeId' in e && e.nodeId === nodeId);
    return { node, state: 'taken', reasons: [], errors };
  }

  const reasons: LockReason[] = [];
  // Mutually exclusive group (capstones): computed directly, because validateLoadout reports the
  // conflict on whichever node sorts later, which may be the one already taken.
  const rival = node.choiceGroup ? nodes.find((id) => id !== nodeId && talentNode(id)?.choiceGroup === node.choiceGroup) : undefined;
  if (rival && node.choiceGroup) reasons.push({ kind: 'choice', group: node.choiceGroup, takenId: rival, takenName: nameOf(rival) });

  for (const e of validateLoadout(lo([...nodes, nodeId]), Number.POSITIVE_INFINITY, maxTier)) {
    if (!('nodeId' in e) || e.nodeId !== nodeId) continue;
    if (e.kind === 'missingPrereq') reasons.push({ kind: 'prereq', prereq: e.prereq, name: nameOf(e.prereq) });
    else if (e.kind === 'rankLocked') reasons.push({ kind: 'rank', tier: e.tier, rankName: rankNameForTalentTier(e.tier) });
    else if (e.kind === 'tierLocked') {
      if (isCapstone(node) && e.needed === CAPSTONE_GATE) reasons.push({ kind: 'capstone', spent: e.spentBefore, needed: e.needed });
      else reasons.push({ kind: 'tier', tier: e.tier, spent: e.spentBefore, needed: e.needed });
    }
  }
  const remaining = points - pointsSpent(nodes);
  if (node.cost > remaining) reasons.push({ kind: 'points', cost: node.cost, remaining: Math.max(0, remaining) });

  const state: NodeState = rival ? 'excluded' : reasons.length ? 'locked' : 'available';
  return { node, state, reasons, errors: [] };
}

export interface TierGate {
  tier: 1 | 2 | 3;
  /** Points needed in lower tiers (0 for tier 1). */
  needed: number;
  spent: number;
  met: boolean;
  /** Rank gate: the tier is above what the current rank allows. */
  rankLocked: boolean;
  rankName: string;
  /** "Requires 5 points in Tier 1 (3/5)" / "Reach Champion to unlock" / "" when open. */
  requirement: string;
}

export function tierGates(nodes: readonly TalentId[], maxTier: number): TierGate[] {
  const picked = nodes.map((id) => talentNode(id)).filter((n): n is TalentNode => !!n);
  return ([1, 2, 3] as const).map((tier) => {
    const needed = TALENT_TIER_GATES[tier - 1] ?? 0;
    const spent = picked.filter((n) => n.tier < tier).reduce((s, n) => s + n.cost, 0);
    const met = spent >= needed;
    const rankLocked = tier > maxTier;
    const rankName = rankNameForTalentTier(tier);
    const below = tier === 2 ? 'Tier 1' : 'Tiers 1–2';
    const requirement = rankLocked ? `Reach ${rankName} rank to unlock` : met ? '' : `Requires ${needed} points in ${below} (${spent}/${needed})`;
    return { tier, needed, spent, met, rankLocked, rankName, requirement };
  });
}

export interface TalentGridView {
  classId: ClassId;
  points: number;
  spent: number;
  remaining: number;
  maxTier: number;
  /** Non-capstone points spent (the capstone gate). */
  nonCapstoneSpent: number;
  gates: TierGate[];
  nodes: NodeView[];
  /** Number of columns (max col + 1). */
  cols: number;
  errors: LoadoutError[];
}

export function talentGrid(classId: ClassId, nodes: readonly TalentId[], points: number, maxTier: number): TalentGridView {
  const tree = talentTree(classId);
  const spent = pointsSpent(nodes);
  const nonCapstoneSpent = nodes
    .map((id) => talentNode(id))
    .filter((n): n is TalentNode => !!n && n.classId === classId && !isCapstone(n))
    .reduce((s, n) => s + n.cost, 0);
  return {
    classId,
    points,
    spent,
    remaining: points - spent,
    maxTier,
    nonCapstoneSpent,
    gates: tierGates(nodes, maxTier),
    nodes: tree.map((n) => nodeView(classId, nodes, n.id, points, maxTier)),
    cols: tree.reduce((m, n) => Math.max(m, n.col + 1), 0),
    errors: validateLoadout({ id: 'grid', name: 'grid', classId, nodes: [...nodes] }, points, maxTier),
  };
}

/** One line of a locked node's tooltip. */
export function lockReasonText(r: LockReason): string {
  switch (r.kind) {
    case 'tier':
      return `Requires ${r.needed} points in ${r.tier === 2 ? 'Tier 1' : 'Tiers 1–2'} (${r.spent}/${r.needed})`;
    case 'capstone':
      return `Capstone: requires ${r.needed} points in non-capstone talents (${r.spent}/${r.needed})`;
    case 'rank':
      return `Tier ${r.tier} unlocks at ${r.rankName} rank`;
    case 'prereq':
      return `Requires ${r.name}`;
    case 'points':
      return `Costs ${r.cost} point${r.cost === 1 ? '' : 's'}; ${r.remaining} available`;
    case 'choice':
      return `Choose one: ${r.takenName} is taken (unlearn it to pick this)`;
  }
}

// ---------------------------------------------------------------------------
// Validation messages
// ---------------------------------------------------------------------------

/** Player-facing text for a validateLoadout error. `nodes` is the loadout (to name a rival). */
export function loadoutErrorMessage(e: LoadoutError, nodes: readonly TalentId[] = []): string {
  switch (e.kind) {
    case 'unknownNode':
      return `Unknown talent "${e.nodeId}" (no longer in the game). Remove it.`;
    case 'wrongClass':
      return `${nameOf(e.nodeId)} belongs to another class.`;
    case 'duplicate':
      return `${nameOf(e.nodeId)} is picked twice.`;
    case 'missingPrereq':
      return `${nameOf(e.nodeId)} requires ${nameOf(e.prereq)}.`;
    case 'tierLocked': {
      const n = talentNode(e.nodeId);
      if (n && isCapstone(n) && e.needed === CAPSTONE_GATE) return `${nameOf(e.nodeId)} is a capstone: spend ${e.needed} points in non-capstone talents first (${e.spentBefore}/${e.needed}).`;
      return `${nameOf(e.nodeId)} needs ${e.needed} points in ${e.tier === 2 ? 'Tier 1' : 'Tiers 1–2'} (${e.spentBefore}/${e.needed}).`;
    }
    case 'rankLocked':
      return `${nameOf(e.nodeId)} is a Tier ${e.tier} talent: reach ${rankNameForTalentTier(e.tier)} rank to use it.`;
    case 'choiceConflict': {
      const other = nodes.find((id) => id !== e.nodeId && talentNode(id)?.choiceGroup === e.group);
      return other ? `Only one capstone: ${nameOf(e.nodeId)} conflicts with ${nameOf(other)}.` : `${nameOf(e.nodeId)} conflicts with another choice.`;
    }
    case 'overBudget':
      return `${e.spent} points spent but only ${e.available} available.`;
  }
}

/** All validation messages of a loadout ([] when valid). */
export function validationMessages(loadout: TalentLoadout, points: number, maxTier: number): string[] {
  return validateLoadout(loadout, points, maxTier).map((e) => loadoutErrorMessage(e, loadout.nodes));
}

// ---------------------------------------------------------------------------
// Editing
// ---------------------------------------------------------------------------

/** Add the node, or remove it if taken (dependents stay; validation then flags them). */
export function toggleNode(nodes: readonly TalentId[], id: TalentId): TalentId[] {
  return nodes.includes(id) ? nodes.filter((x) => x !== id) : [...nodes, id];
}

/** Same picks, any order. */
export function sameNodes(a: readonly TalentId[], b: readonly TalentId[]): boolean {
  if (a.length !== b.length) return false;
  const s = new Set(a);
  return b.every((x) => s.has(x));
}

/** The two saved loadouts the screen edits. ('default' = Recommended, managed by bots.) */
export const SAVED_LOADOUTS: readonly { id: TalentPresetId; name: string }[] = [
  { id: 'trash', name: 'Trash' },
  { id: 'boss', name: 'Boss' },
];
export const RECOMMENDED_LOADOUT_ID = 'default';

export interface CharacterTalentInfo {
  points: number;
  maxTier: number;
  activeLoadoutId: string;
  autoManaged: boolean;
}

export function talentInfo(p: Profile, classId: ClassId): CharacterTalentInfo {
  const ch = p.characters[classId];
  return {
    points: ch?.hero.talentPoints ?? 0,
    maxTier: tierDef(profileStanding(p).tier).talentTiers,
    activeLoadoutId: ch?.activeLoadoutId ?? '',
    autoManaged: ch?.autoManaged ?? false,
  };
}

/** Saved loadout by id, or null. */
export function savedLoadout(p: Profile, classId: ClassId, id: string): TalentLoadout | null {
  return p.characters[classId]?.loadouts.find((l) => l.id === id) ?? null;
}

/** Recommended build for the class at its current points and rank tier. */
export function recommendedNodes(p: Profile, classId: ClassId): TalentId[] {
  const { points, maxTier } = talentInfo(p, classId);
  return defaultLoadout(roleOf(classId), points, maxTier, classId).nodes;
}

/** The named preset trimmed to the character's points (a starting point for an unsaved slot). */
export function presetNodes(p: Profile, classId: ClassId, id: TalentPresetId): TalentId[] {
  const { points, maxTier } = talentInfo(p, classId);
  try {
    return presetLoadout(classId, id, points, maxTier).nodes;
  } catch {
    return [];
  }
}

/** Insert or replace a saved loadout (pure). Optionally make it active. */
export function saveLoadout(p: Profile, classId: ClassId, loadout: { id: string; name: string; nodes: readonly TalentId[] }, opts: { activate?: boolean } = {}): Profile {
  const ch0 = p.characters[classId];
  if (!ch0) return p;
  const next = structuredClone(p);
  const ch = next.characters[classId]!;
  const lo: TalentLoadout = { id: loadout.id, name: loadout.name, classId, nodes: [...loadout.nodes] };
  const i = ch.loadouts.findIndex((l) => l.id === lo.id);
  if (i >= 0) ch.loadouts[i] = lo;
  else ch.loadouts.push(lo);
  if (opts.activate) ch.activeLoadoutId = lo.id;
  return next;
}

/** Make a saved loadout the active one (pure; unknown id → unchanged). */
export function setActiveLoadout(p: Profile, classId: ClassId, id: string): Profile {
  const ch = p.characters[classId];
  if (!ch || !ch.loadouts.some((l) => l.id === id) || ch.activeLoadoutId === id) return p;
  const next = structuredClone(p);
  next.characters[classId]!.activeLoadoutId = id;
  return next;
}

/** Spent points of the active loadout (what the hub's "unspent points" reads). */
export function activeSpent(p: Profile, classId: ClassId): number {
  const ch = p.characters[classId];
  const lo = ch?.loadouts.find((l) => l.id === ch.activeLoadoutId);
  return pointsSpent(lo?.nodes ?? []);
}

// ---------------------------------------------------------------------------
// Instant edits of the ACTIVE loadout (Skills screen: no draft, no Save step)
// ---------------------------------------------------------------------------

export type TalentEdit =
  | { ok: true; profile: Profile; nodes: TalentId[]; /** Dependents dropped by an unlearn. */ removed: TalentId[] }
  | { ok: false; reason: string };

/** The active loadout's picks ([] when the character has none). */
export function activeNodes(p: Profile, classId: ClassId): TalentId[] {
  const ch = p.characters[classId];
  return [...(ch?.loadouts.find((l) => l.id === ch.activeLoadoutId)?.nodes ?? [])];
}

/** Write `nodes` into the active loadout (created as "Recommended" if missing); the player now manages talents. */
export function writeActiveNodes(p: Profile, classId: ClassId, nodes: readonly TalentId[]): Profile {
  const ch0 = p.characters[classId];
  if (!ch0) return p;
  const next = structuredClone(p);
  const ch = next.characters[classId]!;
  const id = ch.activeLoadoutId || RECOMMENDED_LOADOUT_ID;
  const i = ch.loadouts.findIndex((l) => l.id === id);
  if (i >= 0) ch.loadouts[i] = { ...ch.loadouts[i]!, nodes: [...nodes] };
  else ch.loadouts.push({ id, name: id === RECOMMENDED_LOADOUT_ID ? 'Recommended' : id, classId, nodes: [...nodes] });
  ch.activeLoadoutId = id;
  ch.autoManaged = false;
  return next;
}

/** Errors a candidate pick list has that belong to `id` (or the budget). */
function newErrors(classId: ClassId, nodes: readonly TalentId[], id: TalentId, points: number, maxTier: number): LoadoutError[] {
  return validateLoadout({ id: 'edit', name: 'edit', classId, nodes: [...nodes] }, points, maxTier).filter((e) => e.kind === 'overBudget' || ('nodeId' in e && e.nodeId === id));
}

/** Learn a node on the active loadout (a rival capstone is swapped out). Fails with a short reason. */
export function learnNode(p: Profile, classId: ClassId, id: TalentId): TalentEdit {
  const node = talentNode(id);
  if (!p.characters[classId] || !node || node.classId !== classId) return { ok: false, reason: 'Unknown talent' };
  const { points, maxTier } = talentInfo(p, classId);
  const cur = activeNodes(p, classId);
  const v = nodeView(classId, cur, id, points, maxTier);
  if (v.state === 'taken') return { ok: false, reason: 'Already learned' };
  let nodes: TalentId[];
  if (v.state === 'excluded') {
    const other = v.reasons.find((r) => r.kind !== 'choice');
    if (other) return { ok: false, reason: lockReasonText(other) };
    const rival = v.reasons.find((r): r is Extract<LockReason, { kind: 'choice' }> => r.kind === 'choice');
    nodes = [...cur.filter((x) => x !== rival?.takenId), id];
  } else if (v.state === 'locked') return { ok: false, reason: lockReasonText(v.reasons[0]!) };
  else nodes = [...cur, id];
  const errs = newErrors(classId, nodes, id, points, maxTier);
  if (errs.length) return { ok: false, reason: loadoutErrorMessage(errs[0]!, nodes) };
  return { ok: true, profile: writeActiveNodes(p, classId, nodes), nodes, removed: [] };
}

/** Drop picks that no longer validate structurally (missing prereq, tier gate…), repeatedly. */
export function pruneInvalid(classId: ClassId, nodes: readonly TalentId[], maxTier: number): TalentId[] {
  let cur = [...nodes];
  for (let guard = 0; guard < 64; guard++) {
    const bad = new Set(
      validateLoadout({ id: 'prune', name: 'prune', classId, nodes: cur }, Number.POSITIVE_INFINITY, maxTier)
        .filter((e): e is Extract<LoadoutError, { nodeId: TalentId }> => 'nodeId' in e)
        .map((e) => e.nodeId),
    );
    if (!bad.size) return cur;
    cur = cur.filter((x) => !bad.has(x));
  }
  return cur;
}

/** Unlearn a node on the active loadout; dependents that no longer validate are unlearned too. */
export function unlearnNode(p: Profile, classId: ClassId, id: TalentId): TalentEdit {
  if (!p.characters[classId]) return { ok: false, reason: 'Unknown hero' };
  const cur = activeNodes(p, classId);
  if (!cur.includes(id)) return { ok: false, reason: 'Not learned' };
  const { maxTier } = talentInfo(p, classId);
  const nodes = pruneInvalid(classId, cur.filter((x) => x !== id), maxTier);
  const removed = cur.filter((x) => x !== id && !nodes.includes(x));
  return { ok: true, profile: writeActiveNodes(p, classId, nodes), nodes, removed };
}

/** Unlearn everything on the active loadout (respec is free outside combat). */
export function resetTalents(p: Profile, classId: ClassId): TalentEdit {
  if (!p.characters[classId]) return { ok: false, reason: 'Unknown hero' };
  return { ok: true, profile: writeActiveNodes(p, classId, []), nodes: [], removed: [] };
}

/** The recommended build for the current points and rank, on the active loadout (auto stays off). */
export function applyRecommended(p: Profile, classId: ClassId): TalentEdit {
  if (!p.characters[classId]) return { ok: false, reason: 'Unknown hero' };
  const nodes = recommendedNodes(p, classId);
  return { ok: true, profile: writeActiveNodes(p, classId, nodes), nodes, removed: [] };
}

// ---------------------------------------------------------------------------
// "What does this node change, on which button"
// ---------------------------------------------------------------------------
//
// Talent text is generated by the ability tooltip writer (hud/tooltip): the node's transforms are
// applied to the class, and each changed ability is diffed against its untalented form
// ("Cleave hits 3 more targets (up to 8)."), each new ability is described in full, each new
// passive by what it does, and each modified aura by what it gained. That keeps the talent screen
// in the same WoW-style wording as the action-bar tooltips, with exact numbers.

export interface ChangeGroup {
  /** Hotbar key of the ability ("Q"), when bound. */
  key: HotbarKey | null;
  /** Ability (button) or aura name, or null for class-wide passives. */
  ability: string | null;
  /** Full sentences ("Cleave hits 3 more targets (up to 8)."). */
  changes: string[];
}

/** "holy_power" → "Holy Power". */
export const resName = (r: string): string => r.split(/[_\s]+/).map((s) => (s ? s[0]!.toUpperCase() + s.slice(1) : s)).join(' ');

/** Transform ops that edit an aura in place (described once, on the aura, not on every user of it). */
const AURA_OPS: ReadonlySet<ClassTransform['op']> = new Set(['patchAura', 'addProc', 'addAuraMods']);

function keyOf(def: ClassDef, abilityId: string): HotbarKey | null {
  for (const [k, v] of Object.entries(def.binds)) if (v === abilityId) return k as HotbarKey;
  for (const [k, v] of Object.entries(def.extraBinds ?? {})) if (v === abilityId) return k as HotbarKey;
  return null;
}

/** Apply transforms one by one, skipping any that cannot apply to this class (never throws). */
function applySafe(def: ClassDef, ts: readonly ClassTransform[]): ClassDef {
  let cur = def;
  for (const t of ts) {
    try {
      cur = applyTransforms(cur, [t]);
    } catch {
      /* not describable against this class */
    }
  }
  return cur;
}

/** Tooltip content backed by one (compiled) class: its abilities and inline auras by id. */
function classContent(def: ClassDef): TooltipContent {
  return { ability: (id) => def.abilities.find((a) => a.id === id), aura: (id) => findAura(def, id) };
}

const uniq = (xs: readonly string[]): string[] => [...new Set(xs.filter(Boolean))];

/** A talent-added ability, as its tooltip would read: header facts, then what it does. */
function newAbilityLines(a: AbilityDef, def: ClassDef, content: TooltipContent): string[] {
  let m: TooltipModel;
  try {
    m = describeAbility(a, null, content, { classDef: def, baseAbility: null, iconUrl: null });
  } catch {
    return [`Grants ${a.name}.`];
  }
  const facts = [m.cast?.toLowerCase(), m.cost?.replace(/^Generates/, 'generates'), m.cooldown].filter((x): x is string => !!x);
  const out = [`Grants ${a.name}${facts.length ? ` (${facts.join(', ')})` : ''}.`];
  for (const l of m.lines) if (l.tone === 'desc' || l.tone === 'good' || /^(Off the global cooldown|Usable while)/.test(l.text)) out.push(sentence(l.text));
  return uniq(out);
}

/** A diffAbility phrase as a sentence about the ability ("2 charges" → "Shield Slam has 2 charges."). */
function abilitySentence(name: string, phrase: string): string {
  const p = phrase.trim();
  if (p.startsWith(name) || /^the end of /.test(p)) return sentence(p);
  const rules: [RegExp, (m: RegExpExecArray) => string][] = [
    [/^(\d+) charges$/, (m) => `${name} has ${m[1]} charges`],
    [/^no cooldown$/, () => `${name} no longer has a cooldown`],
    [/^(.+) cooldown( \(was .+\))?$/, (m) => `${name} has a ${m[1]} cooldown${m[2] ?? ''}`],
    [/^(costs|generates|no longer generates|deals) /, () => `${name} ${p}`],
    [/^usable /, () => `${name} is ${p}`],
    [/^no longer (usable|requires)/, () => `${name} ${p.replace('requires facing', 'requires facing the target')}`],
    [/^requires /, () => `${name} ${p}`],
    [/^(.+ yd) range( \(was .+\))?$/, (m) => `${name} has a ${m[1]} range${m[2] ?? ''}`],
    [/^off the global cooldown$/, () => `${name} is off the global cooldown`],
    [/^(.+ sec) global cooldown$/, (m) => `${name} has a ${m[1]} global cooldown`],
    [/^targeting changed to (.+)$/, (m) => `${name} now targets ${m[1] === 'enemy' ? 'an enemy' : m[1] === 'ally' ? 'an ally' : m[1]}`],
    [/^(Instant|.+ sec cast|Channeled .+) \(was (.+)\)$/, (m) => `${name} is now ${m[1] === 'Instant' ? 'instant' : m[1]!.toLowerCase()} (was ${m[2]!.toLowerCase()})`],
  ];
  for (const [re, f] of rules) {
    const m = re.exec(p);
    if (m) return sentence(f(m));
  }
  return sentence(p);
}

/** Token-level "(was …)" for two clauses that differ in a few words ("15% (was 12%) chance"); null otherwise. */
function inlineWas(now: string, was: string): string | null {
  const a = now.split(' ');
  const b = was.split(' ');
  let i = 0;
  while (i < a.length && i < b.length && a[i] === b[i]) i++;
  let j = 0;
  while (j < a.length - i && j < b.length - i && a[a.length - 1 - j] === b[b.length - 1 - j]) j++;
  const oldSeg = b.slice(i, b.length - j);
  const newSeg = a.slice(i, a.length - j);
  if (!newSeg.length || !oldSeg.length || oldSeg.length > 2 || newSeg.length > 2) return null;
  return [...a.slice(0, a.length - j), `(was ${oldSeg.join(' ')})`, ...a.slice(a.length - j)].join(' ');
}

/** One aura clause as a talent sentence: "While Consecrated Ground is active, …", "When Divine Purpose is applied, it also …". */
function auraSentence(a: AuraDef, clause: string, mode: 'also' | 'now'): string {
  const word = mode === 'also' ? 'also ' : 'now ';
  let m = /^when applied, (.*)$/.exec(clause);
  if (m) return sentence(`when ${a.name} is applied, it ${word}${m[1]}`);
  m = /^when it (ends|expires), (.*)$/.exec(clause);
  if (m) return sentence(`when ${a.name} ${m[1]}, it ${word}${m[2]}`);
  if (a.flags?.hidden || a.durationMs <= 0) return sentence(clause); // class passive / proc engine
  return sentence(mode === 'also' ? `while ${a.name} is active, ${clause}` : `${a.name}: ${clause}`);
}

/** What changed on one aura (duration, stacks, gained procs/mods/effects) as sentences. */
function auraChangeLines(before: AuraDef, after: AuraDef, ctx: RenderCtx): string[] {
  const out: string[] = [];
  if (before.durationMs !== after.durationMs && after.durationMs > 0)
    out.push(`${after.name} lasts ${fmtDuration(after.durationMs)}${before.durationMs > 0 ? ` instead of ${fmtDuration(before.durationMs)}` : ''}.`);
  const bs = before.stacking === 'stack' ? (before.maxStacks ?? 1) : 1;
  const as = after.stacking === 'stack' ? (after.maxStacks ?? 1) : 1;
  if (as !== bs) out.push(`${after.name} stacks up to ${as} times${bs > 1 ? ` instead of ${bs}` : ''}.`);
  // Everything else: the clauses the aura's tooltip gains (or loses), stacking/duration neutralised.
  const norm = (a: AuraDef): AuraDef => ({ ...a, durationMs: after.durationMs, stacking: after.stacking, maxStacks: after.maxStacks });
  const quiet = { ...ctx, formulas: [] };
  const was = auraClauses(norm(before), quiet);
  const now = auraClauses(norm(after), quiet);
  const gained = now.filter((c) => !was.includes(c));
  const lost = was.filter((c) => !now.includes(c));
  if (gained.length === 1 && lost.length === 1) {
    const inline = inlineWas(gained[0]!, lost[0]!);
    out.push(auraSentence(after, inline ?? gained[0]!, 'now'));
    return out;
  }
  for (const c of gained) out.push(auraSentence(after, c, lost.length ? 'now' : 'also'));
  for (const c of lost) out.push(sentence(`${after.name} no longer: ${c}`));
  return out;
}

/** Split an aura into describable parts (each proc; all mods; the rest) with the one ability each is about. */
function auraParts(a: AuraDef): { aura: AuraDef; abilityId: string | null }[] {
  const single = (ids: readonly (string | undefined)[]): string | null => {
    const u = [...new Set(ids)];
    return u.length === 1 && u[0] ? u[0] : null;
  };
  const parts: { aura: AuraDef; abilityId: string | null }[] = [];
  for (const pr of a.procs ?? []) parts.push({ aura: { ...a, procs: [pr], mods: undefined, periodic: undefined, onApply: undefined, onRemove: undefined }, abilityId: single(pr.abilityIds?.length === 1 ? pr.abilityIds : [undefined]) });
  if (a.mods?.length) parts.push({ aura: { ...a, procs: undefined, periodic: undefined, onApply: undefined, onRemove: undefined }, abilityId: single(a.mods.map((m) => (m.abilityIds?.length === 1 ? m.abilityIds[0] : undefined))) });
  if (a.periodic || a.onApply?.length || a.onRemove?.length || a.absorb) parts.push({ aura: { ...a, procs: undefined, mods: undefined }, abilityId: null });
  return parts.length ? parts : [{ aura: a, abilityId: null }];
}

/**
 * What a node (or any list of transforms) changes, grouped by button:
 * "[3] Cleave → Cleave hits 3 more targets (up to 8)." Never throws: a transform that cannot be
 * applied to `def` is skipped. `def` is the base (uncompiled) ClassDef.
 */
export function describeNode(node: Pick<TalentNode, 'effect'>, def: ClassDef, abilityName?: (id: string) => string): ChangeGroup[] {
  const ts = node.effect;
  const full = applySafe(def, ts);
  const auraOnly = applySafe(def, ts.filter((t) => AURA_OPS.has(t.op)));
  const content = classContent(full);
  const label = (id: string): string => abilityName?.(id) ?? full.abilities.find((a) => a.id === id)?.name ?? id;

  const groups: ChangeGroup[] = [];
  const byKey = new Map<string, ChangeGroup>();
  const add = (gk: string, key: HotbarKey | null, ability: string | null, lines: readonly string[]): void => {
    const ls = uniq(lines);
    if (!ls.length) return;
    let g = byKey.get(gk);
    if (!g) {
      g = { key, ability, changes: [] };
      byKey.set(gk, g);
      groups.push(g);
    }
    for (const l of ls) if (!g.changes.includes(l)) g.changes.push(l);
  };

  // Abilities: new ones in full, changed ones as a diff (aura edits excluded: described once below).
  for (const cur of full.abilities) {
    const ctx = classRenderCtx(cur, content, full);
    const before = auraOnly.abilities.find((a) => a.id === cur.id);
    if (!before) {
      add(cur.id, keyOf(full, cur.id), label(cur.id), newAbilityLines(cur, full, content));
      continue;
    }
    add(cur.id, keyOf(full, cur.id), label(cur.id), diffAbility(before, cur, { ...ctx, formulas: [] }).map((l) => abilitySentence(cur.name, l)));
  }

  // New passives: each proc / mod set under the one button it is about, else class-wide. Passives
  // keyed on a talent-added ability are already part of that ability's description.
  const had = new Set((def.passives ?? []).map((p) => p.id));
  const added = new Set(full.abilities.filter((a) => !auraOnly.abilities.some((b) => b.id === a.id)).map((a) => a.id));
  const passiveCtx = { ...classRenderCtx(null, content, full), formulas: [] };
  for (const p of full.passives ?? []) {
    if (had.has(p.id)) continue;
    for (const part of auraParts({ ...p, durationMs: 0, stacking: 'unique' })) {
      if (part.abilityId && added.has(part.abilityId)) continue;
      const lines = auraClauses(part.aura, passiveCtx).map(sentence);
      const owner = part.abilityId ? full.abilities.find((a) => a.id === part.abilityId) : undefined;
      if (owner) add(owner.id, keyOf(full, owner.id), label(owner.id), lines);
      else add('', null, null, lines);
    }
  }

  // Edited auras (patchAura / addProc / addAuraMods): what the aura gained.
  const auraIds = uniq(ts.flatMap((t) => (AURA_OPS.has(t.op) && 'auraId' in t ? [t.auraId] : [])));
  for (const id of auraIds) {
    const before = findAura(def, id);
    const after = findAura(full, id);
    if (!before || !after) continue;
    const lines = auraChangeLines(before, after, classRenderCtx(null, content, full));
    // An aura named after a button (Last Stand, Ravager's Finale) belongs to that button.
    const owner = full.abilities.find((a) => a.name === after.name);
    if (owner) add(owner.id, keyOf(full, owner.id), label(owner.id), lines);
    else add(`aura:${id}`, null, after.flags?.hidden ? null : after.name, lines);
  }

  // A resize that hits the ticks and the channel end alike reads once ("Bladestorm hits 4 more targets").
  for (const g of groups) {
    g.changes = g.changes
      .filter((l) => !(/^The end of /.test(l) && g.changes.includes(l.replace(/^The end of /, ''))))
      .map((l) => l.replace(/^The end of (.+?) also /, 'When $1 finishes, it also '));
  }

  // Buttons first (hotbar order), then other abilities, then auras/passives.
  const order = ['1', '2', '3', '4', '5', '6', '7', 'Q', 'E', 'R', 'F', 'G'];
  const rank = (g: ChangeGroup): number => (g.key ? order.indexOf(g.key) : g.ability && full.abilities.some((a) => label(a.id) === g.ability) ? 50 : 99);
  return groups.sort((a, b) => rank(a) - rank(b));
}
